import { Flag, Users, FileText, CheckCircle, Phone, Clock, Award } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/hooks/useLanguage";
import { immigrationContent } from "@/i18n/immigration-content";

const CitizenshipNaturalization = () => {
  const { language } = useLanguage();
  const content = immigrationContent.citizenship[language];

  const requirements = [
    "Be at least 18 years old",
    "Be a permanent resident (green card holder) for at least 5 years (or 3 years if married to U.S. citizen)",
    "Demonstrate continuous residence in the U.S.",
    "Show physical presence in the U.S. for required time",
    "Demonstrate good moral character",
    "Pass English and civics tests",
    "Take Oath of Allegiance",
  ];

  const benefits = [
    { icon: <Flag className="h-8 w-8" />, title: "Right to Vote", desc: "Participate in federal, state, and local elections" },
    { icon: <Users className="h-8 w-8" />, title: "Family Immigration", desc: "Sponsor family members for immigration more easily" },
    { icon: <Award className="h-8 w-8" />, title: "Federal Jobs", desc: "Access to federal employment opportunities" },
    { icon: <FileText className="h-8 w-8" />, title: "U.S. Passport", desc: "Travel freely with U.S. passport protection" },
    { icon: <Clock className="h-8 w-8" />, title: "No Renewal Required", desc: "Citizenship is permanent - no card renewals" },
    { icon: <CheckCircle className="h-8 w-8" />, title: "Protection from Deportation", desc: "Cannot be deported from the United States" },
  ];

  const process = [
    { step: "1", title: "Determine Eligibility", desc: "Verify you meet all requirements for naturalization" },
    { step: "2", title: "Prepare Application", desc: "Complete Form N-400 and gather supporting documents" },
    { step: "3", title: "Submit Application", desc: "File application with USCIS and pay fees" },
    { step: "4", title: "Biometrics Appointment", desc: "Attend fingerprinting appointment" },
    { step: "5", title: "Interview & Tests", desc: "Pass English and civics exam, interview with USCIS" },
    { step: "6", title: "Receive Decision", desc: "USCIS approves or denies your application" },
    { step: "7", title: "Oath Ceremony", desc: "Take Oath of Allegiance and receive certificate" },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center bg-gradient-to-br from-primary via-primary-light to-accent overflow-hidden">
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMjAgMjB2MmgtMnYtMmgyem0tMi0yaC0ydjJoMnYtMnptMiAwaDJ2LTJoLTJ2MnptMCAyaDJ2MmgtMnYtMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="inline-block animate-fade-in">
            <Flag className="h-20 w-20 mx-auto mb-6 text-accent animate-pulse" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">{content.title}</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto animate-fade-in opacity-95">
            {content.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
            <Button asChild size="lg" variant="cta" className="hover-scale">
              <Link to="/appointment">Schedule Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/20">
              <a href="tel:8329303059">
                <Phone className="mr-2 h-5 w-5" />
                Call: (832) 930-3059
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-4 text-center">Naturalization Requirements</h2>
            <p className="text-xl text-muted-foreground mb-12 text-center">
              To become a U.S. citizen through naturalization, you must meet these requirements:
            </p>
            <div className="space-y-4">
              {requirements.map((req, index) => (
                <div key={index} className="flex items-start gap-4 bg-card p-6 rounded-lg shadow-md hover-scale transition-all">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-accent" />
                  </div>
                  <span className="text-lg pt-0.5">{req}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">Benefits of U.S. Citizenship</h2>
          <p className="text-xl text-muted-foreground mb-12 text-center max-w-3xl mx-auto">
            U.S. citizenship provides numerous advantages and rights beyond permanent residence
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-card p-8 rounded-lg shadow-lg hover-scale transition-all">
                <div className="text-accent mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">The Naturalization Process</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            {process.map((item, index) => (
              <div key={index} className="flex gap-4 items-start bg-card p-6 rounded-lg shadow-md hover-scale transition-all">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-gold-shine text-primary font-bold text-xl flex items-center justify-center shadow-gold">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <div className="container mx-auto px-4 py-12">
        <QuickContact 
          title="Ready to Apply for Citizenship?"
          description="Let our experienced immigration attorneys guide you through every step of the naturalization process."
        />
      </div>

      {/* Test Preparation */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Citizenship Test Preparation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold mb-4 text-accent">English Test</h3>
                <p className="mb-4 text-muted-foreground">You must demonstrate ability to read, write, and speak basic English.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Speaking: Conversation with USCIS officer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Reading: Read 1 out of 3 sentences correctly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Writing: Write 1 out of 3 sentences correctly</span>
                  </li>
                </ul>
              </div>
              <div className="bg-card p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold mb-4 text-accent">Civics Test</h3>
                <p className="mb-4 text-muted-foreground">You must answer 6 out of 10 questions correctly about U.S. history and government.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>American Government</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>American History</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Integrated Civics (Geography, Symbols, Holidays)</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-4">
                <strong>Note:</strong> Some applicants may qualify for exemptions or modifications based on age and time as a permanent resident.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {content.faq.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg px-6 shadow-md">
                  <AccordionTrigger className="text-lg font-semibold hover:text-accent">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CTASection 
        title="Start Your Journey to U.S. Citizenship Today"
        description="Our experienced immigration team will guide you through every step of the naturalization process."
      />
      <Footer />
    </div>
  );
};

export default CitizenshipNaturalization;
